import {
  getCurrentInstance,
  h,
  onBeforeUnmount,
  render,
  type Component,
  type Ref,
  type VNode
} from 'vue'
import {
  GridStack,
  type AddRemoveFcn,
  type GridStackOptions
} from 'gridstack'

interface VueGridStackBridgeOptions {
  container: Ref<HTMLElement | undefined>
  proxyComponent: Component
  getComponent: (id: string) => Component | null
  getProperties: (id: string) => Record<string, unknown>
  onRemove: (id: string) => void | Promise<void>
}

export function useVueGridStack(options: VueGridStackBridgeOptions) {
  const appContext = getCurrentInstance()?.appContext
  const renderedRoots = new Map<string, HTMLElement>()
  const previousAddRemoveCallback = GridStack.addRemoveCB
  let grid: GridStack | undefined

  const requireGrid = (): GridStack => {
    if (!grid)
      throw new Error('GridStack has not been initialized')

    return grid
  }

  const renderWithGlobals = (
    component: Component,
    props: Record<string, unknown>
  ): VNode => {
    const vnode = h(component, props)
    vnode.appContext = appContext
    return vnode
  }

  const addRemoveCallback: AddRemoveFcn = (host, item, add, isGrid) => {
    if (!host || isGrid || !item.id)
      return undefined

    if (!add) {
      const renderRoot = renderedRoots.get(item.id)
      if (renderRoot) {
        render(null, renderRoot)
        renderedRoots.delete(item.id)
      }
      return undefined
    }

    const itemVNode = renderWithGlobals(options.proxyComponent, {
      onRemove: (payload: { root: HTMLDivElement; id: string }) => {
        requireGrid().removeWidget(payload.root)
        void options.onRemove(payload.id)
      },
      properties: options.getProperties(item.id),
      component: options.getComponent(item.id)
    })

    const renderRoot = document.createElement('div')
    renderedRoots.set(item.id, renderRoot)
    render(itemVNode, renderRoot)
    return itemVNode.el as HTMLElement
  }

  const init = (
    gridOptions: GridStackOptions,
    margin = 4
  ): GridStack => {
    if (!options.container.value)
      throw new Error('GridStack container is unavailable')

    GridStack.addRemoveCB = addRemoveCallback
    grid = GridStack.init(gridOptions, options.container.value)
    grid.margin(margin)
    return grid
  }

  onBeforeUnmount(() => {
    for (const renderRoot of renderedRoots.values())
      render(null, renderRoot)

    renderedRoots.clear()
    grid?.destroy(false)

    if (GridStack.addRemoveCB === addRemoveCallback)
      GridStack.addRemoveCB = previousAddRemoveCallback
  })

  return { init }
}